interface Options<TValue> {
    /** Number of events needed to trigger the threshold */
    eventThreshold: number;
    /** Time window in milliseconds to consider events */
    timeWindowMs: number;
    /** Minimum time between threshold callbacks in milliseconds */
    callbackCooldownMs: number;
    /** Callback function when threshold is met */
    callback: (values: { mostCommon: TValue; last: TValue; first: TValue }) => void;
}

/**
 * Tracks events in a sliding time window and triggers a callback when a threshold is reached.
 * Includes rate limiting to prevent callback spam.
 */
export class SlidingWindowThreshold<TValue> {
    private values: { value: TValue; timestamp: number }[] = [];

    private readonly eventThreshold: number;

    private readonly timeWindowMs: number;

    private readonly callbackCooldownMs: number;

    private readonly callback: (values: { mostCommon: TValue; last: TValue; first: TValue }) => void;

    private lastCallbackTime = 0;

    constructor(options: Options<TValue>) {
        if (options.eventThreshold < 1) {
            throw new Error("Event threshold must be at least 1");
        }
        if (options.timeWindowMs <= 0) {
            throw new Error("Time window must be greater than 0");
        }
        if (options.callbackCooldownMs < 0) {
            throw new Error("Call back cooldown must be equal or greater than 0");
        }
        this.eventThreshold = options.eventThreshold;
        this.timeWindowMs = options.timeWindowMs;
        this.callbackCooldownMs = options.callbackCooldownMs;
        this.callback = options.callback;
    }

    public addEvent(value: TValue) {
        this.values.push({
            value,
            timestamp: performance.now(),
        });
        this.checkThreshold();
    }

    private checkThreshold() {
        const currentTime = performance.now();

        this.values = this.values.filter((value) => value.timestamp >= currentTime - this.timeWindowMs);

        if (this.values.length >= this.eventThreshold && currentTime - this.callbackCooldownMs > this.lastCallbackTime) {
            const mostCommon = this.getMostCommonValue();
            this.callback({
                first: this.values[0].value,
                last: this.values[this.values.length - 1].value,
                mostCommon,
            });
            this.lastCallbackTime = currentTime;
        }
    }

    private getMostCommonValue(): TValue {
        const frequencyMap = new Map<TValue, number>();

        // Count occurrences of each value
        for (const item of this.values) {
            frequencyMap.set(item.value, (frequencyMap.get(item.value) || 0) + 1);
        }

        // Find the value with the highest frequency
        let mostCommon = this.values[0].value;
        let maxCount = 0;

        for (const [value, count] of frequencyMap) {
            if (count > maxCount) {
                maxCount = count;
                mostCommon = value;
            }
        }

        return mostCommon;
    }
}
